import { useState, useEffect, useCallback } from "react";

interface UseScrollCollapseProps {
	initiallyScrolled?: boolean;
	alwaysScrolled?: boolean; // If true, the header is always treated as scrolled
	initiallyFullyCollapsed?: boolean;
	scrollThreshold?: number; // Pixels scrolled down to trigger the 'scrolled' state
}

export function useScrollCollapse({
	initiallyScrolled = false,
	alwaysScrolled = false,
	initiallyFullyCollapsed = false,
	scrollThreshold = 400, // Default threshold of 10px
}: UseScrollCollapseProps = {}) { // Provide default empty object for props
	const [isPageScrolled, setIsPageScrolled] = useState(
		alwaysScrolled || initiallyScrolled
	);
	const [isFullyCollapsed, setIsFullyCollapsed] = useState(
		initiallyFullyCollapsed
	);

	// Effect to track window scroll and update scrolled state
	useEffect(() => {
		// If alwaysScrolled is true, set scrolled state and skip attaching the listener
		if (alwaysScrolled) {
			setIsPageScrolled(true);
			// We might want to ensure the header isn't collapsed if alwaysScrolled becomes true
			// setIsFullyCollapsed(false);
			return; // No listener needed
		}

		// Function to handle scroll events
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const newScrolled = currentScrollY > scrollThreshold;

			// Update scrolled state if it changed
			setIsPageScrolled((prevScrolled) => {
				if (prevScrolled !== newScrolled) {
					// If scrolling back to the top, ensure the header is expanded
					if (!newScrolled) {
						setIsFullyCollapsed(false);
					}
					return newScrolled;
				}
				return prevScrolled; // No change
			});
		};

		// Set initial state based on current scroll position (only if not alwaysScrolled)
		handleScroll();

		// Add scroll event listener
		window.addEventListener("scroll", handleScroll, { passive: true });

		// Cleanup listener on component unmount or when dependencies change
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
		// Rerun effect if alwaysScrolled or scrollThreshold changes
	}, [alwaysScrolled, scrollThreshold]);

	// Callback to manually toggle the fully collapsed state of the header
	const toggleFullyCollapsed = useCallback(() => {
		// Optional: Prevent collapsing if not scrolled (and not alwaysScrolled)?
		// if (!isPageScrolled && !alwaysScrolled) return;
		setIsFullyCollapsed((prev) => !prev);
	}, []); // This callback doesn't depend on external state

	return {
		isPageScrolled,
		isFullyCollapsed,
		toggleFullyCollapsed,
	};
}
